import { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { LogoFull } from "./Logo";
import { API } from "../App";
import axios from "axios";
import { ArrowLeft, Check, Clock, Rocket } from "lucide-react";

const Changelog = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    axios.get(`${API}/changelog`).then(({ data }) => {
      setData(data);
    }).catch(console.error).finally(() => setLoading(false));
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen bg-[#FAFAFA] flex items-center justify-center">
        <p className="text-[15px] text-[#6B7280]">Loading...</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#FAFAFA]">
      {/* Header */}
      <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-40">
        <div className="max-w-[720px] mx-auto px-4 sm:px-6 h-14 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link
              to="/"
              className="text-[#6B7280] hover:text-[#0A0A0A] transition-colors"
              data-testid="changelog-back"
            >
              <ArrowLeft className="w-4 h-4" />
            </Link>
            <LogoFull size={24} />
          </div>
          <Link
            to="/register"
            className="btn-primary text-[13px] px-4 py-2"
            data-testid="changelog-cta"
          >
            Get started free
          </Link>
        </div>
      </header>

      <main className="max-w-[720px] mx-auto px-4 sm:px-6 py-8 sm:py-16">
        {/* Hero */}
        <div className="mb-12 sm:mb-16">
          <h1 className="font-display text-[28px] sm:text-[40px] tracking-[-0.03em] text-[#0A0A0A] mb-3">
            Changelog
          </h1>
          <p className="text-[15px] sm:text-[17px] text-[#6B7280] leading-relaxed max-w-lg">
            What we've shipped and what's coming next. We build in public and ship fast.
          </p>
        </div>

        {/* Shipped */}
        {data?.shipped?.map((release, idx) => (
          <div key={idx} className="mb-10 sm:mb-14" data-testid={`release-${release.version}`}>
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-8 bg-[#10B981] flex items-center justify-center flex-shrink-0">
                <Check className="w-4 h-4 text-white" />
              </div>
              <div>
                <h2 className="font-semibold text-[17px] sm:text-[19px] text-[#0A0A0A]">
                  v{release.version} — {release.title}
                </h2>
                <p className="text-[13px] text-[#9CA3AF] font-mono">{release.date}</p>
              </div>
            </div>
            <div className="ml-11 space-y-2">
              {release.items.map((item, i) => (
                <div key={i} className="flex items-start gap-2.5">
                  <div className="w-1.5 h-1.5 bg-[#D1D5DB] mt-2 flex-shrink-0" />
                  <p className="text-[14px] text-[#374151] leading-relaxed">{item}</p>
                </div>
              ))}
            </div>
          </div>
        ))}

        {/* Divider */}
        <div className="border-t border-[#E5E7EB] my-10 sm:my-14" />

        {/* Upcoming / Roadmap */}
        <div className="mb-8">
          <div className="flex items-center gap-3 mb-6">
            <div className="w-8 h-8 bg-[#1A3DFF] flex items-center justify-center flex-shrink-0">
              <Rocket className="w-4 h-4 text-white" />
            </div>
            <h2 className="font-semibold text-[17px] sm:text-[19px] text-[#0A0A0A]">
              Coming Soon
            </h2>
          </div>
          <div className="space-y-4 ml-11">
            {data?.upcoming?.map((item, idx) => (
              <div
                key={idx}
                className="bg-white border border-[#E5E7EB] p-4 sm:p-5"
                data-testid={`upcoming-${idx}`}
              >
                <div className="flex items-center justify-between mb-1">
                  <h3 className="font-semibold text-[15px] text-[#0A0A0A]">{item.title}</h3>
                  <span className="text-[11px] font-semibold text-[#1A3DFF] uppercase tracking-[0.05em] px-2 py-0.5 bg-[#1A3DFF]/5 border border-[#1A3DFF]/20">
                    {item.status}
                  </span>
                </div>
                <p className="text-[13px] text-[#6B7280] leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#E5E7EB] py-6 text-center">
        <p className="text-[13px] text-[#9CA3AF]">
          Built with care by the VeroBill team.{" "}
          <Link to="/" className="text-[#1A3DFF] hover:underline">Back to home</Link>
        </p>
      </footer>
    </div>
  );
};

export default Changelog;
